import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pill, Loader2, CheckCircle, Pencil, ClipboardCheck, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE_WIDE, Tabs, SearchInput, KpiTile, Dialog, Field, FormRow, Switch, StatusPill, PersonAvatar,
  EmptyState, ErrorState, TableSkeleton, OverflowMenu, fmtAgo, fmtDateTime, num, plural, statusLabel,
} from '../../components/ui';

// Medications: one medication administration record (MAR) for every client, grouped by client,
// with the latest recorded outcome on each line so a team leader sees what was given, refused,
// held or missed without opening anything.

const ROUTES = ['Oral', 'Sublingual', 'Topical', 'Inhaled', 'Injection', 'Nasal', 'Eye drops', 'Ear drops', 'Rectal', 'Other'];
const FORMS = ['Tablet', 'Capsule', 'Liquid', 'Cream', 'Patch', 'Inhaler', 'Injection', 'Drops', 'Other'];
const FREQUENCIES = ['Once daily', 'Twice daily', 'Three times daily', 'Four times daily', 'Every 4 hours', 'Every 6 hours', 'Every 8 hours', 'Every 12 hours', 'Weekly', 'As needed (PRN)', 'Other'];
const SCHEDULES = ['S2', 'S3', 'S4', 'S8', 'Unscheduled'];
const OUTCOMES = ['given', 'refused', 'not_available', 'held', 'prn_not_required'];
const ymd = (v) => (v ? String(v).split('T')[0] : '');

// ── Add / edit a medication ──────────────────────────────────────────────────
function MedDialog({ med, clients, onClose, onSaved }) {
  const isEdit = !!(med && med.id);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(() => ({
    clientId: (med && med.client_id) || '',
    medicationName: (med && med.medication_name) || '',
    genericName: (med && med.generic_name) || '',
    dosage: (med && med.dosage) || '',
    frequency: (med && med.frequency) || '',
    route: (med && med.route) || '',
    form: (med && med.form) || '',
    prescriber: (med && med.prescriber) || '',
    pharmacy: (med && med.pharmacy) || '',
    pharmacyName: (med && med.pharmacy_name) || '',
    startDate: ymd(med && med.start_date),
    endDate: ymd(med && med.end_date),
    instructions: (med && med.instructions) || '',
    isPrn: (med && med.is_prn) || false,
    prnReason: (med && med.prn_reason) || '',
    isActive: !med || med.is_active !== false,
    schedule: (med && med.schedule) || '',
    controlledBalance: med && med.controlled_balance != null ? med.controlled_balance : '',
  }));
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: '' })); };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const next = {};
    if (!form.clientId) next.clientId = 'Choose the client.';
    if (!form.medicationName) next.medicationName = 'Enter the medication name.';
    if (next.clientId || next.medicationName) { setErrors(next); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/medications/${med.id}`, form); toast.success('Medication updated'); }
      else { await api.post('/medications', form); toast.success('Medication added'); }
      onSaved();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not save the medication');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} size="lg" title={isEdit ? `Edit ${med.medication_name}` : 'Add medication'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="medication-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Add medication'}
          </button>
        </>
      )}>
      <form id="medication-form" onSubmit={handleSave} className="space-y-4">
        <Field label="Client" required error={errors.clientId}>
          {(id) => (
            <select id={id} className="input-field" value={form.clientId} onChange={(e) => set('clientId', e.target.value)} disabled={isEdit} aria-invalid={!!errors.clientId}>
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          )}
        </Field>
        <FormRow>
          <Field label="Medication name" required error={errors.medicationName}>
            {(id) => <input id={id} className="input-field" value={form.medicationName} onChange={(e) => set('medicationName', e.target.value)} aria-invalid={!!errors.medicationName} />}
          </Field>
          <Field label="Generic name">
            {(id) => <input id={id} className="input-field" value={form.genericName} onChange={(e) => set('genericName', e.target.value)} />}
          </Field>
        </FormRow>
        <FormRow cols={3}>
          <Field label="Dosage">
            {(id) => <input id={id} className="input-field" placeholder="For example, 10 mg" value={form.dosage} onChange={(e) => set('dosage', e.target.value)} />}
          </Field>
          <Field label="Route">
            {(id) => (
              <select id={id} className="input-field" value={form.route} onChange={(e) => set('route', e.target.value)}>
                <option value="">Choose</option>
                {ROUTES.map((r) => <option key={r}>{r}</option>)}
              </select>
            )}
          </Field>
          <Field label="Form">
            {(id) => (
              <select id={id} className="input-field" value={form.form} onChange={(e) => set('form', e.target.value)}>
                <option value="">Choose</option>
                {FORMS.map((f) => <option key={f}>{f}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
        <Field label="Frequency">
          {(id) => (
            <select id={id} className="input-field" value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
              <option value="">Choose</option>
              {FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
              {form.frequency && !FREQUENCIES.includes(form.frequency) && <option>{form.frequency}</option>}
            </select>
          )}
        </Field>
        <FormRow>
          <Field label="Prescriber">
            {(id) => <input id={id} className="input-field" value={form.prescriber} onChange={(e) => set('prescriber', e.target.value)} />}
          </Field>
          <Field label="Pharmacy">
            {(id) => <input id={id} className="input-field" value={form.pharmacy} onChange={(e) => set('pharmacy', e.target.value)} />}
          </Field>
          <Field label="Pharmacy name">
            {(id) => <input id={id} className="input-field" placeholder="Dispensing pharmacy" value={form.pharmacyName} onChange={(e) => set('pharmacyName', e.target.value)} />}
          </Field>
          <Field label="Drug schedule">
            {(id) => (
              <select id={id} className="input-field" value={form.schedule} onChange={(e) => set('schedule', e.target.value)}>
                <option value="">Not scheduled</option>
                {SCHEDULES.map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
        {form.schedule === 'S8' && (
          <Field label="Controlled drug balance (units)" hint="Current stock count. Each recorded S8 dose is taken off this balance.">
            {(id) => <input id={id} type="number" step="0.01" className="input-field tabular-nums" placeholder="Current stock count" value={form.controlledBalance} onChange={(e) => set('controlledBalance', e.target.value)} />}
          </Field>
        )}
        <FormRow>
          <Field label="Start date">
            {(id) => <input id={id} type="date" className="input-field" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />}
          </Field>
          <Field label="End date">
            {(id) => <input id={id} type="date" className="input-field" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />}
          </Field>
        </FormRow>
        <Field label="Instructions">
          {(id) => <textarea id={id} className="input-field min-h-[70px] resize-y" value={form.instructions} onChange={(e) => set('instructions', e.target.value)} />}
        </Field>
        <div className="rounded-xl border border-slate-200 px-4 divide-y divide-slate-100">
          <div className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-[13px] font-medium text-slate-800">PRN (as needed)</span>
            <Switch checked={form.isPrn} onChange={(v) => set('isPrn', v)} label="PRN (as needed)" />
          </div>
          {isEdit && (
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-[13px] font-medium text-slate-800">Active</span>
              <Switch checked={form.isActive} onChange={(v) => set('isActive', v)} label="Active" />
            </div>
          )}
        </div>
        {form.isPrn && (
          <Field label="PRN reason or trigger">
            {(id) => <textarea id={id} className="input-field min-h-[50px] resize-y" placeholder="For example, give when pain score is 5 or more" value={form.prnReason} onChange={(e) => set('prnReason', e.target.value)} />}
          </Field>
        )}
      </form>
    </Dialog>
  );
}

// ── Record an administration ─────────────────────────────────────────────────
// The signature rules are the safeguard: every administration is signed with the carer's typed
// full name, and an S8 (controlled) drug also needs a witness and the witness's typed signature.
// Record stays disabled until they are filled; the server checks them again.
function AdministerDialog({ med, staff, onClose, onSaved }) {
  const { user } = useAuth();
  const isS8 = med && med.schedule === 'S8';
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});
  const [form, setForm] = useState({
    status: 'given', outcome: 'given', doseGiven: (med && med.dosage) || '', notes: '',
    signatureData: '', witnessId: '', witnessSignature: '', effectivenessReview: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const errors = {
    signatureData: !form.signatureData.trim() ? 'Type your full name to sign.' : '',
    witnessId: isS8 && !form.witnessId ? 'S8 medications need a witness.' : '',
    witnessSignature: isS8 && !form.witnessSignature.trim() ? 'The witness types their full name to sign.' : '',
  };
  const missing = !!(errors.signatureData || errors.witnessId || errors.witnessSignature);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (missing) { setTouched({ signatureData: true, witnessId: true, witnessSignature: true }); return; }
    setSaving(true);
    try {
      await api.post(`/medications/${med.id}/administer`, form);
      toast.success('Administration recorded');
      onSaved();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not record the administration');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} size="md" title="Record administration"
      description={[med.medication_name, med.dosage].filter(Boolean).join(' — ')}
      footer={(
        <>
          {missing && <span className="mr-auto text-[12px] text-slate-500">{isS8 ? 'Signatures from you and a witness are needed' : 'Sign to record'}</span>}
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="administer-form" disabled={saving || missing} className="btn-primary">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Record
          </button>
        </>
      )}>
      <form id="administer-form" onSubmit={handleSave} className="space-y-4">
        {isS8 && <span className="badge-red">S8 controlled drug</span>}
        <fieldset>
          <legend className="text-[13px] font-medium text-slate-700 mb-1.5">Outcome</legend>
          <div role="radiogroup" aria-label="Outcome" className="grid grid-cols-2 gap-2">
            {OUTCOMES.map((o) => {
              const on = form.outcome === o;
              return (
                <button key={o} type="button" role="radio" aria-checked={on} onClick={() => set('outcome', o)}
                  className={clsx('h-10 px-3 text-[13px] font-medium rounded-lg border text-left transition-colors duration-150',
                    on ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50')}>
                  {statusLabel(o)}
                </button>
              );
            })}
          </div>
        </fieldset>
        <Field label="Dose given">
          {(id) => <input id={id} className="input-field" value={form.doseGiven} onChange={(e) => set('doseGiven', e.target.value)} />}
        </Field>
        <Field label="Notes">
          {(id) => <textarea id={id} className="input-field min-h-[60px] resize-y" value={form.notes} onChange={(e) => set('notes', e.target.value)} />}
        </Field>
        <Field label="Effectiveness review">
          {(id) => <textarea id={id} className="input-field min-h-[50px] resize-y" placeholder="Optional notes on how well the medication worked" value={form.effectivenessReview} onChange={(e) => set('effectivenessReview', e.target.value)} />}
        </Field>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <Field label="Carer signature (type your full name)" required error={touched.signatureData ? errors.signatureData : ''}>
            {(id) => (
              <input id={id} className="input-field" autoComplete="off" aria-invalid={!!(touched.signatureData && errors.signatureData)}
                placeholder={`For example, ${[user && user.first_name, user && user.last_name].filter(Boolean).join(' ') || 'your full name'}`}
                value={form.signatureData} onChange={(e) => set('signatureData', e.target.value)} onBlur={() => touch('signatureData')} />
            )}
          </Field>
        </div>
        {isS8 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-3">
            <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-700"><AlertTriangle size={14} /> S8 controlled drug — witness required</p>
            <Field label="Witness" required error={touched.witnessId ? errors.witnessId : ''}>
              {(id) => (
                <select id={id} className="input-field" value={form.witnessId} aria-invalid={!!(touched.witnessId && errors.witnessId)}
                  onChange={(e) => set('witnessId', e.target.value)} onBlur={() => touch('witnessId')}>
                  <option value="">Choose the witness</option>
                  {(staff || []).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Witness signature (type full name)" required error={touched.witnessSignature ? errors.witnessSignature : ''}>
              {(id) => (
                <input id={id} className="input-field" autoComplete="off" placeholder="Witness full name" aria-invalid={!!(touched.witnessSignature && errors.witnessSignature)}
                  value={form.witnessSignature} onChange={(e) => set('witnessSignature', e.target.value)} onBlur={() => touch('witnessSignature')} />
              )}
            </Field>
          </div>
        )}
      </form>
    </Dialog>
  );
}

// ── MAR table cells ──────────────────────────────────────────────────────────
function LastRecorded({ m }) {
  if (!m.last_outcome && !m.last_recorded_at) return <span className="text-slate-500">Nothing recorded yet</span>;
  const when = m.last_recorded_at ? new Date(m.last_recorded_at) : null;
  return (
    <div className="leading-tight">
      <StatusPill status={m.last_outcome} />
      <div className="text-[12px] text-slate-500 mt-1 whitespace-nowrap" title={when ? fmtDateTime(when) : undefined}>
        {when ? fmtAgo(when) : ''}{m.last_recorded_by ? ` · ${m.last_recorded_by}` : ''}
      </div>
    </div>
  );
}

const TH = 'py-2.5 px-4 text-left text-[12px] font-semibold text-slate-500 whitespace-nowrap';

export default function Medications() {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [modal, setModal] = useState(null);      // null | {} (new) | medication
  const [adminModal, setAdmin] = useState(null);  // null | medication
  const [filter, setFilter] = useState('active');
  const [clientFilter, setClientFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const p = new URLSearchParams();
    if (filter === 'active') p.set('active', 'true');
    if (filter === 'inactive') p.set('active', 'false');
    if (clientFilter) p.set('client_id', clientFilter);
    api.get(`/medications?${p}`)
      .then((r) => setMeds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [filter, clientFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/clients').then((r) => setClients((r.data && r.data.clients) || r.data || [])).catch(() => {});
    api.get('/staff').then((r) => setStaff((r.data && r.data.staff) || r.data || [])).catch(() => {});
  }, []);

  const closeMed = useCallback(() => setModal(null), []);
  const closeAdmin = useCallback(() => setAdmin(null), []);
  const savedMed = useCallback(() => { setModal(null); load(); }, [load]);
  const savedAdmin = useCallback(() => { setAdmin(null); load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return meds;
    return meds.filter((m) => [m.medication_name, m.generic_name, m.prescriber, m.client_name]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [meds, search]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((m) => {
      const key = m.client_id || m.client_name;
      if (!map.has(key)) map.set(key, { key, clientId: m.client_id, name: m.client_name || 'Unknown client', meds: [] });
      map.get(key).meds.push(m);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered]);

  // Last 7 days, across the medications in view.
  const week = useMemo(() => filtered.reduce((t, m) => ({
    refused: t.refused + (Number(m.refused_7d) || 0) + (Number(m.missed_7d) || 0),
    held: t.held + (Number(m.held_7d) || 0),
    prn: t.prn + (m.is_prn ? (Number(m.given_7d) || 0) : 0),
  }), { refused: 0, held: 0, prn: 0 }), [filtered]);

  const activeCount = meds.filter((m) => m.is_active).length;
  const filtersOn = !!(clientFilter || search.trim() || filter !== 'active');
  const clearFilters = () => { setFilter('active'); setClientFilter(''); setSearch(''); };

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center min-w-0">
          <Tabs variant="segmented" ariaLabel="Show medications" value={filter} onChange={setFilter}
            tabs={[{ id: 'all', label: 'All' }, { id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }]} />
          <label htmlFor="med-client" className="sr-only">Client</label>
          <select id="med-client" className="input-field sm:!w-52" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <SearchInput value={search} onChange={setSearch} placeholder="Search medication, prescriber or client" className="w-full sm:w-72" />
          <span className="text-[13px] text-slate-500 tabular-nums whitespace-nowrap" aria-live="polite">
            {loading ? 'Loading…' : search.trim() ? `${num(filtered.length)} of ${plural(meds.length, 'medication')}` : filter === 'inactive' ? plural(meds.length, 'inactive medication') : plural(activeCount, 'active medication')}
          </span>
        </div>
        <button type="button" onClick={() => setModal({})} className="btn-primary self-start lg:self-auto"><Plus size={16} /> Add medication</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiTile label="Refused or missed (7 days)" value={loading ? '…' : num(week.refused)} icon={AlertTriangle} alert={!loading && week.refused > 0} accent="slate" />
        <KpiTile label="Held or not available (7 days)" value={loading ? '…' : num(week.held)} accent={week.held > 0 ? 'amber' : 'slate'} />
        <div className="col-span-2 lg:col-span-1">
          <KpiTile label="PRN doses given (7 days)" value={loading ? '…' : num(week.prn)} icon={Pill} accent="slate" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <TableSkeleton rows={7} cols={6} />
          : loadError ? <ErrorState message="We couldn't load medications. Check your connection, then try again." onRetry={load} />
          : groups.length === 0 ? (
            filtersOn
              ? <EmptyState icon={Pill} title="No medications match these filters" action={<button type="button" onClick={clearFilters} className="btn-secondary btn-sm">Clear filters</button>} />
              : <EmptyState icon={Pill} title="No medications recorded yet" hint="Add each client's medications so staff can record what was given, refused or held."
                  action={<button type="button" onClick={() => setModal({})} className="btn-primary btn-sm"><Plus size={14} /> Add medication</button>} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                <caption className="sr-only">Medication administration record, grouped by client</caption>
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className={TH}>Medication</th>
                    <th scope="col" className={TH}>Dose and route</th>
                    <th scope="col" className={TH}>Frequency</th>
                    <th scope="col" className={TH}>Last recorded</th>
                    <th scope="col" className={TH}>PRN</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={clsx(TH, 'text-right')}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                {groups.map((g) => (
                  <tbody key={g.key}>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th scope="rowgroup" colSpan={7} className="px-4 py-2 text-left font-normal">
                        <span className="flex items-center gap-2.5">
                          <PersonAvatar id={g.clientId} name={g.name} size="xs" />
                          <span className="text-[13px] font-semibold text-slate-900">{g.name}</span>
                          <span className="text-[12px] text-slate-500">{plural(g.meds.length, 'medication')}</span>
                        </span>
                      </th>
                    </tr>
                    {g.meds.map((m) => (
                      <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors duration-150">
                        <td className="py-2.5 px-4 align-top">
                          <button type="button" onClick={() => setModal(m)}
                            className="text-left font-semibold text-slate-900 hover:text-primary-700 hover:underline underline-offset-2">
                            {m.medication_name}
                          </button>
                          <div className="text-[12px] text-slate-500">
                            {[m.generic_name, m.prescriber ? `Prescribed by ${m.prescriber}` : null].filter(Boolean).join(' · ')}
                          </div>
                          {m.schedule === 'S8' && <span className="badge-red mt-1">S8</span>}
                        </td>
                        <td className="py-2.5 px-4 align-top">
                          <div className="text-slate-800">{m.dosage || '—'}</div>
                          {(m.route || m.form) && <div className="text-[12px] text-slate-500">{[m.route, m.form].filter(Boolean).join(' · ')}</div>}
                        </td>
                        <td className="py-2.5 px-4 align-top text-slate-700">{m.frequency || '—'}</td>
                        <td className="py-2.5 px-4 align-top"><LastRecorded m={m} /></td>
                        <td className="py-2.5 px-4 align-top">
                          {m.is_prn ? (
                            <div className="leading-tight">
                              <span className="badge-gray">PRN</span>
                              {Number(m.given_7d) > 0 && <div className="text-[12px] text-slate-500 mt-1 whitespace-nowrap">{plural(Number(m.given_7d), 'dose')} in 7 days</div>}
                            </div>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className="py-2.5 px-4 align-top"><StatusPill status={m.is_active ? 'active' : 'inactive'} /></td>
                        <td className="py-2 px-4 align-top text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button type="button" onClick={() => setAdmin(m)} className="btn-secondary btn-sm" aria-label={`Record administration of ${m.medication_name} for ${g.name}`}>
                              <ClipboardCheck size={13} /> Record
                            </button>
                            <OverflowMenu label={`More actions for ${m.medication_name}`} size={16} className="p-2" items={[
                              { label: 'Edit medication', icon: Pencil, onClick: () => setModal(m) },
                              { label: 'Record administration', icon: ClipboardCheck, onClick: () => setAdmin(m) },
                            ]} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}
      </div>

      {modal !== null && <MedDialog med={modal} clients={clients} onClose={closeMed} onSaved={savedMed} />}
      {adminModal !== null && <AdministerDialog med={adminModal} staff={staff} onClose={closeAdmin} onSaved={savedAdmin} />}
    </div>
  );
}
